/**
 * GoogleOAuthCallback - Handles Google OAuth callback for custom credentials
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

import { API } from '@/components/portal/PortalContext';

const GoogleOAuthCallback = () => {
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);
  
  useEffect(() => {
    let isMounted = true;

    const safeJsonParse = (raw) => {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };

    const processCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code') || new URLSearchParams(window.location.hash.replace(/^#/, '')).get('code');
      const errorParam = params.get('error') || new URLSearchParams(window.location.hash.replace(/^#/, '')).get('error');
      const stateValue = params.get('state') || new URLSearchParams(window.location.hash.replace(/^#/, '')).get('state');
      const mobileFlow = stateValue === 'mobile_google_auth' || safeJsonParse(stateValue)?.platform === 'mobile';
      
      console.log('[GoogleOAuthCallback] Processing callback, code exists:', !!code);
      
      if (errorParam) {
        setError(`Google Login abgebrochen: ${errorParam}`);
        setProcessing(false);
        if (mobileFlow) {
          window.location.href = `strongfit://auth/google?error=${encodeURIComponent(`Google Login abgebrochen: ${errorParam}`)}`;
          return;
        }
        setTimeout(() => {
          if (window.opener && window.opener !== window) {
            try {
              window.opener.postMessage({
                type: 'google_oauth_error',
                error: `Google Login abgebrochen: ${errorParam}`
              }, window.location.origin);
            } catch (e) { /* ignore */ }
            window.close();
          } else {
            window.location.href = '/portal';
          }
        }, 2000);
        return;
      }
      
      if (!code) {
        setError('Kein Autorisierungscode erhalten');
        setProcessing(false);
        setTimeout(() => {
          window.location.href = '/portal';
        }, 1500);
        return;
      }
      
      const callbackPayload = {
        code: code,
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        redirect_uri: window.location.origin + '/portal/google/callback'
      };

      // Prevent duplicate processing for the same code
      const globalKey = `__sf_google_oauth_cb_${code}`;
      if (window[globalKey]) {
        console.log('[GoogleOAuthCallback] Already processing this code, skipping');
        return;
      }
      window[globalKey] = true;

      console.log('[GoogleOAuthCallback] Sending callback to backend...');

      try {
        const response = await axios.post(
          `${API}/portal/auth/google/callback`,
          callbackPayload,
          { timeout: 30000 }
        );

        if (!isMounted) return;

        const { token, refresh_token, customer, is_new_user } = response.data;
        
        console.log('[GoogleOAuthCallback] Success! is_new_user:', is_new_user, 'customer:', customer?.first_name);
        
        if (mobileFlow) {
          window.location.href = `strongfit://auth/google?token=${encodeURIComponent(token)}&refresh_token=${encodeURIComponent(refresh_token || '')}&is_new_user=${is_new_user ? '1' : '0'}`;
          return;
        }

        // IMPORTANT: Always store token in localStorage FIRST as backup
        // This ensures login works even if postMessage fails
        localStorage.setItem('customerToken', token);
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
        console.log('[GoogleOAuthCallback] Token saved to localStorage');
        
        // Check if we're in a popup
        if (window.opener && window.opener !== window) {
          console.log('[GoogleOAuthCallback] In popup, sending message to parent...');
          try {
            window.opener.postMessage({
              type: 'google_oauth_success',
              token: token,
              refresh_token: refresh_token,
              customer: customer,
              is_new_user: is_new_user
            }, window.location.origin);
            
            // Give parent time to process, then close
            setTimeout(() => {
              window.close();
            }, 500);
          } catch (e) {
            console.error('[GoogleOAuthCallback] postMessage failed:', e);
            // Fallback: redirect the popup itself
            window.location.href = '/portal';
          }
        } else {
          // Direct navigation - token already stored, just redirect
          console.log('[GoogleOAuthCallback] Direct navigation, redirecting to portal...');
          window.location.href = '/portal';
        }
      } catch (err) {
        if (!isMounted) return;
        
        console.error('[GoogleOAuthCallback] Error:', err);
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        
        // Check if this is an "invalid_grant" error - code was already used
        const isCodeAlreadyUsed = detail?.includes('invalid_grant') || detail?.includes('already used');
        
        if (isCodeAlreadyUsed) {
          // The user was likely already created, check if we have a token in localStorage
          const existingToken = localStorage.getItem('customerToken');
          if (existingToken) {
            console.log('[GoogleOAuthCallback] Code was already used, but we have a token. Redirecting...');
            window.location.href = '/portal';
            return;
          }
        }
        
        setError(detail || (status ? `Login fehlgeschlagen (Status ${status})` : 'Login fehlgeschlagen'));
        setProcessing(false);
        
        // Close popup after error
        if (mobileFlow) {
          window.location.href = `strongfit://auth/google?error=${encodeURIComponent(detail || 'Login fehlgeschlagen')}`;
          return;
        }

        setTimeout(() => {
          if (window.opener && window.opener !== window) {
            try {
              window.opener.postMessage({
                type: 'google_oauth_error',
                error: detail || 'Login fehlgeschlagen'
              }, window.location.origin);
            } catch (e) { /* ignore */ }
            window.close();
          } else {
            window.location.href = '/portal';
          }
        }, 2000);
      }
    };
    
    processCallback();

    return () => {
      isMounted = false;
    };
  }, []);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="text-center">
        {error ? (
          <div className="text-red-400">
            <p className="text-lg font-medium mb-2">Fehler</p>
            <p className="text-sm">{error}</p>
            <p className="text-xs text-gray-500 mt-4">Sie werden in Kürze weitergeleitet...</p>
          </div>
        ) : (
          <div className="text-white">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-yellow-500" />
            <p className="text-lg">Anmeldung wird verarbeitet...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
